/** AI match briefs on the dashboard (betup-server-api docs/match-briefs.md §5). */

export type MatchBriefStatus = 'PENDING' | 'RESEARCHING' | 'READY' | 'FAILED';

/** What the app sees now. */
export type MatchBriefAppStatus = 'ready' | 'pending' | 'not_covered' | 'after_kickoff';

export interface MatchBriefMatchDto {
    id: number;
    sport_id?: number | null;
    league?: string | null;
    home?: string | null;
    away?: string | null;
    start: string;
}

export interface MatchBriefFactor {
    text: string;
    favours?: 'home' | 'away' | 'neutral' | string;
    source?: string;
}

/** The English brief the synthesis wrote. */
export interface MatchBriefJson {
    headline?: string;
    summary?: string;
    market_view?: string;
    pick?: string;
    confidence?: string;
    probabilities?: { home?: number; draw?: number; away?: number };
    totals?: { part?: string; line?: number; over?: number }[];
    halves?: { part?: string; home?: number; draw?: number; away?: number }[];
    factors?: MatchBriefFactor[];
    research_used?: boolean;
}

/** One translation: the brief's texts only. */
export interface MatchBriefTranslation {
    headline?: string;
    summary?: string;
    market_view?: string;
    factors?: string[];
}

export interface MatchBriefDto {
    id: number;
    status: MatchBriefStatus;
    app_status: MatchBriefAppStatus;
    match: MatchBriefMatchDto;
    bets: number;
    attempts: number;
    error?: string | null;
    research_run_id?: string | null;
    /** The match research (qbet_match_research) it was written from. */
    research_id?: number | null;
    headline?: string | null;
    pick?: string | null;
    confidence?: string | null;
    research_used?: boolean | null;
    languages: string[];
    created_at: string;
    updated_at: string;
    ready_at?: string | null;
    // One brief only.
    brief?: MatchBriefJson | null;
    translations?: Record<string, MatchBriefTranslation> | null;
    findings?: Record<string, unknown> | null;
    researched_at?: string | null;
    previous_id?: number | null;
    next_id?: number | null;
}

export interface MatchBriefPageDto {
    items: MatchBriefDto[];
    offset: number;
    limit: number;
    totalItems: number;
}

/** An invocation's stage, in order; COMPLETED and FAILED end it. */
export type MatchBriefExecutionStatus =
    'NOT_STARTED' | 'MATCH_ASSIGNED' | 'COLLECTING_CONTEXT' | 'WEB_RESEARCH' | 'SYNTHESIS' | 'TRANSLATIONS' | 'COMPLETED' | 'FAILED';

export const MATCH_BRIEF_STAGES: MatchBriefExecutionStatus[] =
    ['NOT_STARTED', 'MATCH_ASSIGNED', 'COLLECTING_CONTEXT', 'WEB_RESEARCH', 'SYNTHESIS', 'TRANSLATIONS', 'COMPLETED', 'FAILED'];

/** What a trigger asks for: the whole brief, or a new web research of the match only. */
export type MatchBriefScope = 'BRIEF' | 'RESEARCH';

/** One AI match brief invocation: a trigger taken by Crons' listener (qbet_match_brief_execution). */
export interface MatchBriefExecutionDto {
    id: number;
    /** ON_DEMAND: a player asked Ace for it (its calls run in workflow match.brief.on-demand). */
    source: 'SCHEDULED' | 'MANUAL' | 'ON_DEMAND' | string;
    scope: MatchBriefScope;
    requested_by: string | null;
    requested_match_id: number | null;
    match: { id: number; sport_id: number | null; league: string | null; home: string | null; away: string | null; start: string | null } | null;
    brief_id: number | null;
    status: MatchBriefExecutionStatus;
    failed_stage: MatchBriefExecutionStatus | null;
    error: string | null;
    note: string | null;
    /** The AI hub workflow run key of all its calls (workflow match.brief). */
    correlation_id: string | null;
    research_run_id: string | null;
    /** The match research it made, or reused from another invocation (research_reused), and when that research ran. */
    research_id: number | null;
    research_reused: boolean;
    researched_at: string | null;
    cost_usd: number | null;
    stages: { stage: MatchBriefExecutionStatus; at: string }[] | null;
    created_at: string;
    updated_at: string;
    finished_at: string | null;
}

export interface MatchBriefExecutionPageDto {
    items: MatchBriefExecutionDto[];
    offset: number;
    limit: number;
    totalItems: number;
    failures_24h: number;
    max_failures_24h: number;
    /** Triggers taken but not finished. */
    running: number;
}

/** qbet_app_settings keys of the briefs (MatchBriefSettingKeys in betup-server-api). */
export const MATCH_BRIEF_SETTING_KEYS = {
    enabled: 'match_brief.enabled',
    sportIds: 'match_brief.sport_ids',
    maxMatches24h: 'match_brief.max_matches_24h',
    intervalMinutes: 'match_brief.interval_minutes',
    windowHours: 'match_brief.window_hours',
    minBets: 'match_brief.min_bets',
    maxMatchesPerRun: 'match_brief.max_matches_per_run',
    maxFailures24h: 'match_brief.max_failures_24h',
    researchMaxAgeHours: 'match_brief.research_max_age_hours',
} as const;

/** One researcher finding (qbet_match_research findings[]). */
export interface MatchResearchFinding {
    type?: string;
    team?: 'home' | 'away' | 'both' | string;
    claim?: string;
    status?: 'confirmed' | 'rumour' | string;
    source?: string;
    published?: string;
}

/** One web research run of a match (qbet_match_research). */
export interface MatchResearchDto {
    id: number;
    match: { id: number; sport_id: number | null; league: string | null; home: string | null; away: string | null; start: string | null };
    run_id: string | null;
    correlation_id: string | null;
    /** The invocation that made it. */
    execution_id: number | null;
    match_confirmed: boolean;
    findings_count: number;
    cost_usd: number | null;
    researched_at: string;
    // One research only.
    findings?: { match_confirmed?: boolean; competition?: string; date?: string; findings?: MatchResearchFinding[] } | null;
}

export interface MatchResearchPageDto {
    items: MatchResearchDto[];
    offset: number;
    limit: number;
    totalItems: number;
}

/** One match's AI state for its page: each part null when there is none. */
export interface MatchAiStatusDto {
    match_id: number;
    brief: { id: number; status: MatchBriefStatus; app_status: MatchBriefAppStatus; headline: string | null; ready_at: string | null; research_id: number | null } | null;
    research_count: number;
    latest_research: MatchResearchDto | null;
    running: MatchBriefExecutionDto | null;
}
